import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  api,
  CommunicationAgentRow,
  CommunicationByAgentResponse,
  CommunicationCampaign,
  CommunicationMessageItem,
  CommunicationOverviewResponse,
} from '../../services/api';

interface CommunicationTrackingDialogProps {
  open: boolean;
  onClose: () => void;
}

const CHART_COLORS = ['#ED8936', '#38B2AC', '#4299E1', '#ECC94B', '#9F7AEA', '#F56565', '#48BB78', '#F6AD55'];

type CommTab = 'overview' | 'campaigns' | 'byAgent';
type PresetId = 'today' | 'last7' | 'last30' | 'thisMonth' | 'lastMonth' | 'allTime';

function formatInputDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function buildRange(id: PresetId): { startDate: string; endDate: string } {
  const today = new Date();
  const todayFloor = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  switch (id) {
    case 'today':
      return { startDate: formatInputDate(todayFloor), endDate: formatInputDate(todayFloor) };
    case 'last7':
      return { startDate: formatInputDate(addDays(todayFloor, -6)), endDate: formatInputDate(todayFloor) };
    case 'last30':
      return { startDate: formatInputDate(addDays(todayFloor, -29)), endDate: formatInputDate(todayFloor) };
    case 'thisMonth':
      return { startDate: formatInputDate(startOfMonth(todayFloor)), endDate: formatInputDate(todayFloor) };
    case 'lastMonth': {
      const firstThis = startOfMonth(todayFloor);
      const lastEnd = addDays(firstThis, -1);
      return { startDate: formatInputDate(startOfMonth(lastEnd)), endDate: formatInputDate(lastEnd) };
    }
    case 'allTime':
      return { startDate: '2025-01-01', endDate: formatInputDate(todayFloor) };
  }
}

const PRESETS: Array<{ id: PresetId; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last30', label: 'Last 30 days' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'allTime', label: 'All time' },
];

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-SG', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-SG', { year: 'numeric', month: 'short', day: '2-digit' });
}

const CommunicationTrackingDialog: React.FC<CommunicationTrackingDialogProps> = ({ open, onClose }) => {
  const muiTheme = useMuiTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const chartAxisStroke = isDark ? 'rgba(255,255,255,0.3)' : 'rgba(31,41,68,0.24)';
  const chartGridStroke = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(31,41,68,0.12)';
  const chartTickStyle = useMemo(
    () => ({ fill: muiTheme.palette.text.secondary, fontSize: 12 }),
    [muiTheme.palette.text.secondary]
  );
  const chartTickStyleSmall = useMemo(
    () => ({ fill: muiTheme.palette.text.secondary, fontSize: 11 }),
    [muiTheme.palette.text.secondary]
  );
  const chartLabelStyle = useMemo(
    () => ({ fill: muiTheme.palette.text.primary, fontSize: 12 }),
    [muiTheme.palette.text.primary]
  );
  const tooltipContentStyle = useMemo(
    () => ({
      backgroundColor: isDark ? 'rgba(20,28,43,0.96)' : '#ffffff',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
      borderRadius: 6,
      color: muiTheme.palette.text.primary,
    }),
    [isDark, muiTheme.palette.text.primary]
  );
  const tooltipLabelStyle = useMemo(
    () => ({ color: muiTheme.palette.text.primary, fontWeight: 600 }),
    [muiTheme.palette.text.primary]
  );
  const tooltipItemStyle = useMemo(
    () => ({ color: muiTheme.palette.text.secondary }),
    [muiTheme.palette.text.secondary]
  );

  const [tab, setTab] = useState<CommTab>('overview');
  const [presetId, setPresetId] = useState<PresetId>('last30');
  const [range, setRange] = useState(buildRange('last30'));

  const [overview, setOverview] = useState<CommunicationOverviewResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<string>('');

  const [campaigns, setCampaigns] = useState<CommunicationCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);
  const [campaignFilter, setCampaignFilter] = useState<string>('');
  const [selectedCampaign, setSelectedCampaign] = useState<CommunicationCampaign | null>(null);
  const [campaignDetails, setCampaignDetails] = useState<CommunicationMessageItem[]>([]);
  const [campaignDetailsLoading, setCampaignDetailsLoading] = useState(false);
  const [campaignDetailsError, setCampaignDetailsError] = useState<string | null>(null);

  const [agentQuery, setAgentQuery] = useState<string>('');
  const [agents, setAgents] = useState<CommunicationAgentRow[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<CommunicationAgentRow | null>(null);
  const [agentDetails, setAgentDetails] = useState<CommunicationByAgentResponse | null>(null);
  const [agentDetailsLoading, setAgentDetailsLoading] = useState(false);
  const [agentDetailsError, setAgentDetailsError] = useState<string | null>(null);

  const handlePresetChange = useCallback((id: PresetId) => {
    setPresetId(id);
    setRange(buildRange(id));
  }, []);

  const handleDateChange = useCallback((field: 'startDate' | 'endDate', value: string) => {
    setRange((prev) => ({ ...prev, [field]: value }));
    setPresetId('last30');
  }, []);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const data = await api.getAdminCommunicationOverview({
        startDate: range.startDate,
        endDate: range.endDate,
      });
      setOverview(data);
      if (selectedChannel && !data.channels.includes(selectedChannel)) {
        setSelectedChannel('');
      }
    } catch (err: any) {
      setOverviewError(err?.message || 'Failed to load overview');
    } finally {
      setOverviewLoading(false);
    }
  }, [range.startDate, range.endDate, selectedChannel]);

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    setCampaignsError(null);
    try {
      const data = await api.getAdminCommunicationCampaigns({
        startDate: range.startDate,
        endDate: range.endDate,
        channel: selectedChannel || undefined,
      });
      setCampaigns(data.campaigns);
    } catch (err: any) {
      setCampaignsError(err?.message || 'Failed to load campaigns');
    } finally {
      setCampaignsLoading(false);
    }
  }, [range.startDate, range.endDate, selectedChannel]);

  const loadCampaignDetails = useCallback(async (campaign: CommunicationCampaign) => {
    setCampaignDetailsLoading(true);
    setCampaignDetailsError(null);
    try {
      const data = await api.getAdminCommunicationCampaignDetails({
        startDate: range.startDate,
        endDate: range.endDate,
        purpose: campaign.purpose,
        channel: campaign.channel,
      });
      setCampaignDetails(data.items);
    } catch (err: any) {
      setCampaignDetailsError(err?.message || 'Failed to load campaign details');
    } finally {
      setCampaignDetailsLoading(false);
    }
  }, [range.startDate, range.endDate]);

  const loadAgents = useCallback(async (q?: string) => {
    setAgentsLoading(true);
    setAgentsError(null);
    try {
      const data = await api.getAdminCommunicationAgents({
        startDate: range.startDate,
        endDate: range.endDate,
        q: q ?? agentQuery,
      });
      setAgents(data.agents);
    } catch (err: any) {
      setAgentsError(err?.message || 'Failed to load agents');
    } finally {
      setAgentsLoading(false);
    }
  }, [range.startDate, range.endDate, agentQuery]);

  const loadAgentDetails = useCallback(async (agent: CommunicationAgentRow) => {
    setAgentDetailsLoading(true);
    setAgentDetailsError(null);
    try {
      const data = await api.getAdminCommunicationByAgent({
        startDate: range.startDate,
        endDate: range.endDate,
        ceaNumber: agent.ceaNumber,
      });
      setAgentDetails(data);
    } catch (err: any) {
      setAgentDetailsError(err?.message || 'Failed to load agent details');
    } finally {
      setAgentDetailsLoading(false);
    }
  }, [range.startDate, range.endDate]);

  useEffect(() => {
    if (!open) return;
    if (tab === 'overview') {
      loadOverview();
    } else if (tab === 'campaigns') {
      loadCampaigns();
    } else if (tab === 'byAgent') {
      if (selectedAgent) {
        loadAgentDetails(selectedAgent);
      } else {
        loadAgents();
      }
    }
  }, [open, tab, range.startDate, range.endDate, selectedChannel]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) {
      setSelectedCampaign(null);
      setSelectedAgent(null);
    }
  }, [open]);

  const filteredCampaigns = useMemo(() => {
    const q = campaignFilter.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter((c) =>
      c.purpose.toLowerCase().includes(q) || c.channel.toLowerCase().includes(q)
    );
  }, [campaigns, campaignFilter]);

  const channelList = useMemo(() => overview?.channels || [], [overview]);

  const seriesData = useMemo(() => {
    if (!overview) return [] as Array<Record<string, any>>;
    return overview.series.map((s) => {
      const total = channelList.reduce((sum, ch) => sum + (Number(s[ch]) || 0), 0);
      return { ...s, __total: total };
    });
  }, [overview, channelList]);

  const visibleChannels = useMemo(
    () => (selectedChannel ? [selectedChannel] : channelList),
    [selectedChannel, channelList]
  );

  const channelColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    channelList.forEach((ch, idx) => { map[ch] = CHART_COLORS[idx % CHART_COLORS.length]; });
    return map;
  }, [channelList]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'var(--color-bg)',
          color: 'var(--color-text)',
          borderBottom: '1px solid rgba(var(--color-text-rgb), 0.1)',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Communication Tracking</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
            Track communications sent to agents across channels and campaigns
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: 'var(--color-bg)', color: 'var(--color-text)', p: 0 }}>
        <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Preset</InputLabel>
              <Select
                label="Preset"
                value={presetId}
                onChange={(e) => handlePresetChange(e.target.value as PresetId)}
              >
                {PRESETS.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="date"
              label="Start Date"
              value={range.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="End Date"
              value={range.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {tab === 'overview' && channelList.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Channel Filter</InputLabel>
                <Select
                  label="Channel Filter"
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e.target.value)}
                >
                  <MenuItem value="">All Channels</MenuItem>
                  {channelList.map((ch) => (
                    <MenuItem key={ch} value={ch}>{ch}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<RefreshIcon />}
              size="small"
              variant="outlined"
              onClick={() => {
                if (tab === 'overview') loadOverview();
                else if (tab === 'campaigns') {
                  setSelectedCampaign(null);
                  loadCampaigns();
                } else if (tab === 'byAgent') {
                  if (selectedAgent) loadAgentDetails(selectedAgent);
                  else loadAgents();
                }
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        <Tabs
          value={tab}
          onChange={(_e, v) => {
            setTab(v as CommTab);
            setSelectedCampaign(null);
            setSelectedAgent(null);
          }}
          sx={{ px: 2.5, borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)' }}
        >
          <Tab value="overview" label="Overview" />
          <Tab value="campaigns" label="Campaigns" />
          <Tab value="byAgent" label="By Agent" />
        </Tabs>

        {tab === 'overview' && (
          <Box sx={{ p: 2.5 }}>
            {overviewError && <Alert severity="error" sx={{ mb: 2 }}>{overviewError}</Alert>}
            {overviewLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
            ) : overview ? (
              <Stack spacing={2.5}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                      Total Sent
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'var(--color-accent)' }}>
                      {overview.total.toLocaleString()}
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                      Channels Used
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                      {overview.channels.length}
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                    <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                      Date Range
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                      {formatDate(range.startDate)} → {formatDate(range.endDate)}
                    </Typography>
                  </Paper>
                </Stack>

                <Paper sx={{ p: 2, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Communications Sent Over Time
                  </Typography>
                  {seriesData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={340}>
                      <LineChart data={seriesData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                        <XAxis dataKey="date" stroke={chartAxisStroke} tick={chartTickStyle} />
                        <YAxis stroke={chartAxisStroke} tick={chartTickStyle} />
                        <RechartsTooltip
                          contentStyle={tooltipContentStyle}
                          labelStyle={tooltipLabelStyle}
                          itemStyle={tooltipItemStyle}
                        />
                        <Legend wrapperStyle={chartLabelStyle} />
                        {visibleChannels.map((ch) => (
                          <Line
                            key={ch}
                            type="monotone"
                            dataKey={ch}
                            stroke={channelColorMap[ch]}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            activeDot={{ r: 6 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
                      No data for selected period
                    </Typography>
                  )}
                </Paper>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>By Channel</Typography>
                    {overview.channelTotals.length > 0 ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie
                            data={overview.channelTotals}
                            dataKey="count"
                            nameKey="channel"
                            cx="50%"
                            cy="50%"
                            outerRadius={90}
                            label={(e: any) => `${e.channel}: ${e.count}`}
                            style={chartLabelStyle}
                          >
                            {overview.channelTotals.map((entry, idx) => (
                              <Cell key={entry.channel} fill={channelColorMap[entry.channel] || CHART_COLORS[idx % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            contentStyle={tooltipContentStyle}
                            labelStyle={tooltipLabelStyle}
                            itemStyle={tooltipItemStyle}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 4, textAlign: 'center' }}>
                        No data
                      </Typography>
                    )}
                  </Paper>
                  <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Channel Breakdown</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Channel</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Share</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {overview.channelTotals.map((ch) => {
                            const pct = overview.total > 0 ? ((ch.count / overview.total) * 100).toFixed(1) : '0';
                            return (
                              <TableRow key={ch.channel} hover>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={ch.channel}
                                    sx={{ bgcolor: channelColorMap[ch.channel], color: '#fff', fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell align="right">{ch.count.toLocaleString()}</TableCell>
                                <TableCell align="right">{pct}%</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Stack>
              </Stack>
            ) : null}
          </Box>
        )}

        {tab === 'campaigns' && (
          <Box sx={{ p: 2.5 }}>
            {selectedCampaign ? (
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    size="small"
                    onClick={() => { setSelectedCampaign(null); setCampaignDetails([]); }}
                  >
                    Back to campaigns
                  </Button>
                </Stack>
                <Paper sx={{ p: 2, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedCampaign.purpose}</Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <Chip size="small" label={selectedCampaign.channel} sx={{ bgcolor: channelColorMap[selectedCampaign.channel] || '#718096', color: '#fff' }} />
                    <Typography variant="body2">Total sent: <strong>{selectedCampaign.count.toLocaleString()}</strong></Typography>
                    <Typography variant="body2">Recipients: <strong>{selectedCampaign.recipients.toLocaleString()}</strong></Typography>
                    <Typography variant="body2">Last sent: <strong>{formatDateTime(selectedCampaign.lastSentAt)}</strong></Typography>
                  </Stack>
                </Paper>
                {campaignDetailsError && <Alert severity="error">{campaignDetailsError}</Alert>}
                {campaignDetailsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
                ) : (
                  <Paper sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                    <TableContainer sx={{ maxHeight: 560 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Sent At</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>CEA</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Agent</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Channel</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {campaignDetails.map((item) => (
                            <TableRow key={item.id} hover>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(item.createdAt)}</TableCell>
                              <TableCell>{item.ceaNumber}</TableCell>
                              <TableCell>{item.agentName || '-'}</TableCell>
                              <TableCell>
                                <Chip size="small" label={item.channel} sx={{ bgcolor: channelColorMap[item.channel] || '#718096', color: '#fff' }} />
                              </TableCell>
                              <TableCell>{item.mobile || item.email || '-'}</TableCell>
                              <TableCell sx={{ maxWidth: 400 }}>
                                <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                  {item.message || '-'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                          {campaignDetails.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                                No messages found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    size="small"
                    placeholder="Search campaigns..."
                    value={campaignFilter}
                    onChange={(e) => setCampaignFilter(e.target.value)}
                    sx={{ minWidth: 260 }}
                  />
                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                    {filteredCampaigns.length} campaign{filteredCampaigns.length === 1 ? '' : 's'}
                  </Typography>
                </Stack>
                {campaignsError && <Alert severity="error">{campaignsError}</Alert>}
                {campaignsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
                ) : (
                  <>
                    {filteredCampaigns.length > 0 && (
                      <Paper sx={{ p: 2, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Top Campaigns by Volume</Typography>
                        <ResponsiveContainer width="100%" height={260}>
                          <BarChart data={filteredCampaigns.slice(0, 10)} layout="vertical" margin={{ left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                            <XAxis type="number" stroke={chartAxisStroke} tick={chartTickStyle} />
                            <YAxis type="category" dataKey="purpose" stroke={chartAxisStroke} tick={chartTickStyleSmall} width={180} />
                            <RechartsTooltip
                              contentStyle={tooltipContentStyle}
                              labelStyle={tooltipLabelStyle}
                              itemStyle={tooltipItemStyle}
                            />
                            <Bar dataKey="count" fill="#ED8936" name="Sent" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Paper>
                    )}
                    <Paper sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                      <TableContainer sx={{ maxHeight: 520 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Purpose (Campaign)</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Channel</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Sent</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>Recipients</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>First Sent</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Last Sent</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredCampaigns.map((c) => (
                              <TableRow
                                key={`${c.purpose}-${c.channel}`}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => { setSelectedCampaign(c); loadCampaignDetails(c); }}
                              >
                                <TableCell sx={{ fontWeight: 500 }}>{c.purpose}</TableCell>
                                <TableCell>
                                  <Chip size="small" label={c.channel} sx={{ bgcolor: channelColorMap[c.channel] || '#718096', color: '#fff' }} />
                                </TableCell>
                                <TableCell align="right">{c.count.toLocaleString()}</TableCell>
                                <TableCell align="right">{c.recipients.toLocaleString()}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(c.firstSentAt)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(c.lastSentAt)}</TableCell>
                              </TableRow>
                            ))}
                            {filteredCampaigns.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                                  No campaigns found
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </>
                )}
              </Stack>
            )}
          </Box>
        )}

        {tab === 'byAgent' && (
          <Box sx={{ p: 2.5 }}>
            {selectedAgent && agentDetails ? (
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    size="small"
                    onClick={() => { setSelectedAgent(null); setAgentDetails(null); }}
                  >
                    Back to agents
                  </Button>
                </Stack>
                <Paper sx={{ p: 2, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {agentDetails.agent.name || agentDetails.agent.ceaNumber}
                  </Typography>
                  <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                    <Typography variant="body2">CEA: <strong>{agentDetails.agent.ceaNumber}</strong></Typography>
                    {agentDetails.agent.mobile && <Typography variant="body2">Mobile: <strong>{agentDetails.agent.mobile}</strong></Typography>}
                    {agentDetails.agent.email && <Typography variant="body2">Email: <strong>{agentDetails.agent.email}</strong></Typography>}
                    <Typography variant="body2">Total received: <strong>{agentDetails.items.length.toLocaleString()}</strong></Typography>
                  </Stack>
                </Paper>

                {agentDetailsError && <Alert severity="error">{agentDetailsError}</Alert>}
                {agentDetailsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
                ) : (
                  <>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                      <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>By Channel</Typography>
                        {agentDetails.channelBreakdown.length > 0 ? (
                          <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                              <Pie
                                data={agentDetails.channelBreakdown}
                                dataKey="count"
                                nameKey="channel"
                                outerRadius={70}
                                label={(e: any) => `${e.channel}: ${e.count}`}
                                style={chartLabelStyle}
                              >
                                {agentDetails.channelBreakdown.map((entry, idx) => (
                                  <Cell key={entry.channel} fill={channelColorMap[entry.channel] || CHART_COLORS[idx % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <RechartsTooltip
                                contentStyle={tooltipContentStyle}
                                labelStyle={tooltipLabelStyle}
                                itemStyle={tooltipItemStyle}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 3, textAlign: 'center' }}>No data</Typography>
                        )}
                      </Paper>
                      <Paper sx={{ p: 2, flex: 1, bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Top Campaigns Received</Typography>
                        {agentDetails.purposeBreakdown.length > 0 ? (
                          <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={agentDetails.purposeBreakdown.slice(0, 8)} layout="vertical" margin={{ left: 10 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke={chartGridStroke} />
                              <XAxis type="number" stroke={chartAxisStroke} tick={chartTickStyleSmall} />
                              <YAxis type="category" dataKey="purpose" stroke={chartAxisStroke} tick={chartTickStyleSmall} width={140} />
                              <RechartsTooltip
                                contentStyle={tooltipContentStyle}
                                labelStyle={tooltipLabelStyle}
                                itemStyle={tooltipItemStyle}
                              />
                              <Bar dataKey="count" fill="#38B2AC" />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', py: 3, textAlign: 'center' }}>No data</Typography>
                        )}
                      </Paper>
                    </Stack>

                    <Paper sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                      <TableContainer sx={{ maxHeight: 520 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Sent At</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Channel</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Message</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {agentDetails.items.map((item) => (
                              <TableRow key={item.id} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(item.createdAt)}</TableCell>
                                <TableCell>
                                  <Chip size="small" label={item.channel} sx={{ bgcolor: channelColorMap[item.channel] || '#718096', color: '#fff' }} />
                                </TableCell>
                                <TableCell>{item.purpose}</TableCell>
                                <TableCell>{item.mobile || item.email || '-'}</TableCell>
                                <TableCell sx={{ maxWidth: 400 }}>
                                  <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                    {item.message || '-'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                            {agentDetails.items.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                                  No messages received
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <TextField
                    size="small"
                    placeholder="Search by CEA, name, mobile or email..."
                    value={agentQuery}
                    onChange={(e) => setAgentQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') loadAgents(); }}
                    sx={{ minWidth: 320 }}
                  />
                  <Button variant="contained" size="small" onClick={() => loadAgents()}>
                    Search
                  </Button>
                  <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
                    {agents.length} agent{agents.length === 1 ? '' : 's'}
                  </Typography>
                </Stack>
                {agentsError && <Alert severity="error">{agentsError}</Alert>}
                {agentsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
                ) : (
                  <Paper sx={{ bgcolor: 'rgba(var(--color-text-rgb), 0.03)' }}>
                    <TableContainer sx={{ maxHeight: 560 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>CEA</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Mobile</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>Received</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Last Sent</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {agents.map((a) => (
                            <TableRow
                              key={a.ceaNumber}
                              hover
                              sx={{ cursor: 'pointer' }}
                              onClick={() => { setSelectedAgent(a); loadAgentDetails(a); }}
                            >
                              <TableCell sx={{ fontWeight: 500 }}>{a.ceaNumber}</TableCell>
                              <TableCell>{a.name || '-'}</TableCell>
                              <TableCell>{a.mobile || '-'}</TableCell>
                              <TableCell>{a.email || '-'}</TableCell>
                              <TableCell align="right">{a.count.toLocaleString()}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(a.lastSentAt)}</TableCell>
                            </TableRow>
                          ))}
                          {agents.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'rgba(var(--color-text-rgb), 0.6)' }}>
                                No agents found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                )}
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CommunicationTrackingDialog;
